/**
 * De copiloot (ontwerp v4 §4) — rechts, op elk scherm.
 *
 * Kop met de kostenregel en de plafondbalk, het strookje "n
 * voorstel(len) wachten op je antwoord", de draad met berichten,
 * meldingen en regelvoorstellen, en onderaan de invoer in Claude-stijl.
 * Geen microfoon.
 *
 * De motor is dezelfde als die van het sparvenster: streaming over SSE,
 * bronnen dichtgeklapt, en een afgekapt antwoord dat blijft staan met
 * "Ga verder". Wat deze schil eromheen zet is de zaak in plaats van de
 * mail, de kostenregel en het gesprek over regelvoorstellen.
 *
 * Twee harde regels van deze repo staan hier in code:
 * - **Uitgeschakeld = uitleggen waarom.** Elke knop die niet kan, staat
 *   uit mét de reden in beeld — nooit verborgen, nooit alleen een toast.
 * - **De copiloot gaat nooit richting de klant.** Een concept komt
 *   hooguit via de reactietegel bij JP terecht, en dat is zijn klik.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Bot,
  ChevronDown,
  CornerDownLeft,
  Loader2,
  Plus,
} from "lucide-react";

import { vertaalApiFout } from "@/lib/api";

import { v6Sleutels } from "../../WerkplekV6/sleutels";

import {
  type CopilootBericht,
  type CopilootMelding,
  type CopilootVoorstel,
  type DierveldVoorstel,
  type ModelKnop,
  copilootApi,
  copilootQueryKey,
  euro,
  maandNaam,
} from "./copilootApi";
import type { KijkContext } from "./kijktMee";
import { LesKaart, LesTeller } from "./LesKaart";
import { isLeerDit, lesTeLang } from "./leerDit";
import { useLesDraad } from "./useLesDraad";
import { useCopilootStream } from "./useCopilootStream";

import { zonderRetourBlok } from "./streamTekst";
import "./copiloot.css";

/** Tot deze hoogte groeit het invoerveld mee; daarna scrolt het intern. */
const INVOER_MAX_HOOGTE = 160;
/** Een les ("leer dit ...") is vaak langer: dan mag het veld hoger. */
const INVOER_MAX_HOOGTE_LES = 280;

/** Waarom de bijlageknop nog niet kan. Staat in beeld, niet in een tip. */
const REDEN_BIJLAGE =
  "Een bijlage meesturen kan nog niet: de copiloot heeft daar nog geen " +
  "plek voor. Foto's van de klant staan wel bij het bericht.";

/** Een hele `<dier>`- of `<dierveld>`-tag: precies die naam, gevolgd
 *  door `>`, `/` of witruimte. `<dieren>` en `<dier-info>` vallen erbuiten. */
const DIER_TAG = /<(\/?)(dier|dierveld)(?=[\s>/])[^<>]*>/gi;
/** Een dier-tag die nog niet af is: alleen aan het einde (half gestreamd). */
const DIER_TAG_HALF = /<\/?(?:dier|dierveld)(?:[\s/][^<>]*)?$/i;
/** Een sluittag, direct na een JSON-blok. */
const DIER_SLUIT_DIRECT = /^\s*<\/(?:dier|dierveld)\s*>/i;
const DIER_SLUIT = /<\/(?:dier|dierveld)\s*>/i;

/** Waar een JSON-object eindigt dat op `start` begint, of null als het
 *  (nog) niet af is. Houdt rekening met accolades binnen strings. */
function eindVanJson(tekst: string, start: number): number | null {
  let diepte = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < tekst.length; i++) {
    const teken = tekst[i];
    if (inString) {
      if (escape) escape = false;
      else if (teken === "\\") escape = true;
      else if (teken === '"') inString = false;
      continue;
    }
    if (teken === '"') inString = true;
    else if (teken === "{") diepte++;
    else if (teken === "}") {
      diepte--;
      if (diepte === 0) return i + 1;
    }
  }
  return null;
}

/**
 * Dier-tags uit de tekst halen (sessie 73 blok 4, fixronde recensie).
 *
 * - Een blok met JSON-inhoud (begint met `{`) is een opdracht aan de
 *   server en verdwijnt helemaal, ook half gestreamd; tekst erna blijft.
 * - Een `<dierveld>`-blok met gewone inhoud is ook een opdracht: weg.
 * - `<dier>Rex</dier>`: alleen de tags weg, "Rex" blijft.
 * - Een open tag zonder sluittag midden in de tekst: alleen die tag weg.
 * - Een tag die nog niet af is, alleen aan het einde: weg.
 */
function schoonDierTags(tekst: string): string {
  if (!/<\/?dier/i.test(tekst)) return tekst;
  const tag = new RegExp(DIER_TAG.source, "gi");
  let uit = "";
  let pos = 0;
  let match: RegExpExecArray | null;
  while ((match = tag.exec(tekst)) !== null) {
    uit += tekst.slice(pos, match.index);
    pos = match.index + match[0].length;
    const losseTag = match[1] === "/" || match[0].endsWith("/>");
    if (losseTag) continue;
    const rest = tekst.slice(pos);
    const inhoudStart = pos + (rest.length - rest.trimStart().length);
    if (tekst[inhoudStart] === "{") {
      const eind = eindVanJson(tekst, inhoudStart);
      if (eind !== null) {
        const sluit = DIER_SLUIT_DIRECT.exec(tekst.slice(eind));
        pos = eind + (sluit ? sluit[0].length : 0);
      } else {
        const sluit = DIER_SLUIT.exec(rest);
        // Geen sluittag en geen einde aan de JSON: nog aan het streamen.
        pos = sluit ? pos + sluit.index + sluit[0].length : tekst.length;
      }
      tag.lastIndex = pos;
      continue;
    }
    if (match[2].toLowerCase() === "dierveld") {
      const sluit = DIER_SLUIT.exec(rest);
      if (sluit) {
        pos += sluit.index + sluit[0].length;
        tag.lastIndex = pos;
      }
    }
  }
  uit += tekst.slice(pos);
  return uit.replace(DIER_TAG_HALF, "");
}

/** Kale JSON met `huisdier_id` weghalen; wat erna komt blijft staan.
 *  Is de JSON nog niet af (streamen), dan gaat de rest weg. */
function knipKaleDierJson(tekst: string): string {
  const kaal = /\{\s*"huisdier_id"/i;
  let uit = tekst;
  let match: RegExpExecArray | null;
  while ((match = kaal.exec(uit)) !== null) {
    const eind = eindVanJson(uit, match.index);
    uit = uit.slice(0, match.index) + (eind === null ? "" : uit.slice(eind));
  }
  return uit;
}

/** Concept- en onderwerpmarkeringen uit de live-stream halen, zodat JP
 *  nooit rauwe tags ziet. Het definitieve bericht komt na de stream
 *  gestructureerd terug (server-side geparsed). */
function formatteerStreamTekst(tekst: string): string {
  const zonderOnderwerp = zonderRetourBlok(tekst)
    .replace(/<onderwerp>[\s\S]*?<\/onderwerp>/gi, "")
    .replace(/<onderwerp>[\s\S]*$/i, "");
  // Sessie 73 blok 2 en 4: het dierveld-blok is een opdracht aan de
  // server, nooit tekst voor JP; ook niet half tijdens het streamen.
  const zonderDier = schoonDierTags(zonderOnderwerp)
    // Controle blok 2: ook een voorstel als ```json-blok of kale JSON.
    .replace(/```(?:json)?\s*\{\s*"huisdier_id"[\s\S]*?```/gi, "");
  return knipKaleDierJson(zonderDier)
    .replace(/```(?:json)?\s*$/i, "")
    .replace(/<\/?concept>/gi, "\n")
    .replace(/<\/?[a-z]{0,9}$/i, "")
    .trim();
}

/** Eindweergave (sessie 73 blok 4): achtergebleven dier-tags wegpoetsen
 *  met dezelfde regels als tijdens het streamen. Alleen de tagnamen
 *  `dier` en `dierveld`; andere tekst blijft precies staan. */
function poetsDierTags(tekst: string): string {
  tekst = zonderRetourBlok(tekst);
  if (!/<\/?dier/i.test(tekst)) return tekst;
  return schoonDierTags(tekst).replace(/\n{3,}/g, "\n\n").trim();
}

function tijd(moment: string): string {
  return new Date(moment).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  });
}

/** Datum en tijd in Amsterdam. Momenten reizen als UTC; alleen hier
 *  worden ze omgezet, want JP leest geen UTC (API-contract §1). */
function datumTijd(moment: string): string {
  return new Date(moment).toLocaleString("nl-NL", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  });
}

/** Window-event waarmee de klantkaart de copiloot iets laat vragen. */
const COPILOOT_VRAAG = "copiloot:vraag";

interface CopilootVraag {
  soort: "klant";
  id: string;
  tekst: string;
}

interface Props {
  /** Waar de copiloot naar meekijkt (sessie 46, B6). */
  context: KijkContext;
}

interface Groep {
  label: string | null;
  berichten: CopilootBericht[];
}

/**
 * Sessie 47 (8a): verdeel het gesprek in "eerder" (andere pagina's of
 * kaarten, elk met zijn eigen kop) en "nu" (de beurten sinds de laatste
 * wissel naar de huidige context). Beurten van vóór sessie 47 hebben geen
 * context; die tellen als eerder, zonder label.
 */
export function groepeerPerContext(
  berichten: CopilootBericht[],
  context: KijkContext,
): { oud: Groep[]; nu: CopilootBericht[] } {
  const sleutel = (soort?: string | null, id?: string | null) =>
    `${soort ?? "?"}:${id ?? ""}`;
  const huidig = sleutel(context.soort, context.id);
  // De huidige groep begint na de laatste beurt uit een andere context.
  let start = berichten.length;
  for (let i = berichten.length - 1; i >= 0; i--) {
    if (sleutel(berichten[i].context_soort, berichten[i].context_id) !== huidig) break;
    start = i;
  }
  const oud: Groep[] = [];
  for (const bericht of berichten.slice(0, start)) {
    const label = bericht.context_label ?? null;
    const laatste = oud[oud.length - 1];
    if (laatste && laatste.label === label) laatste.berichten.push(bericht);
    else oud.push({ label, berichten: [bericht] });
  }
  return { oud, nu: berichten.slice(start) };
}

export function WerkplekCopiloot({ context }: Props) {
  const draad = useQuery({
    queryKey: copilootQueryKey(context),
    queryFn: () => copilootApi.draad(context),
    // Sessie 73 (controle blok 1, punt 1): geen vorige draad als
    // placeholder; bij een wissel mag nooit het gesprek van een andere
    // klant of zaak in beeld blijven, ook niet even.
  });
  const stream = useCopilootStream(context);
  const [invoer, setInvoer] = useState("");
  const [knop, setKnop] = useState<ModelKnop>(null);
  const [fout, setFout] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const invoerRef = useRef<HTMLTextAreaElement>(null);
  // Sessie 73 blok 2: een dierveld dat de copiloot wil zetten, en de
  // regels "Zojuist gedaan: ..." die daarna in de draad staan. Ze horen
  // bij deze context: bij een wissel van klant gaan ze weg.
  const [dierveld, setDierveld] = useState<DierveldVoorstel | null>(null);
  const [gedaan, setGedaan] = useState<string[]>([]);
  // Sessie 84: "leer dit ..." gaat niet naar de stream maar naar leer-dit.
  // De lessen horen bij deze context: bij een wissel gaan ze weg, en de
  // open lessen van de nieuwe context komen van de server.
  const {
    beurten: lessen,
    bezig: lesBezig,
    stuur: maakLes,
    sluit: sluitLes,
    laadFout: lessenLaadFout,
  } = useLesDraad(context);
  const contextSleutel = `${context.soort}:${context.id ?? ""}`;
  // Recensie blok 2: de huidige context, voor antwoorden die binnenkomen
  // nadat JP al van klant wisselde.
  const huidigeContext = useRef(contextSleutel);
  useEffect(() => {
    huidigeContext.current = contextSleutel;
    setDierveld(null);
    setGedaan([]);
  }, [contextSleutel]);

  const data = draad.data;
  const berichten = data?.berichten ?? [];
  const voorstellen = data?.voorstellen ?? [];
  const meldingen = data?.meldingen ?? [];
  const liveTekst = formatteerStreamTekst(stream.streamTekst);

  // De teller telt op hetzelfde criterium als de lijst eronder: het
  // aantal open voorstellen, niet een aftreksom.
  const openVoorstellen = voorstellen.filter((v) => v.open);

  const laatste = berichten[berichten.length - 1];
  const afgekapt =
    !stream.bezig &&
    !draad.isFetching &&
    laatste?.rol === "sanne" &&
    laatste.afgekapt === true;
  const gestrand =
    !stream.bezig && !draad.isFetching && laatste?.rol === "gebruiker";

  // Sessie 47 (8a): de beurten in groepen per context. Alles vanaf de
  // laatste beurt in de huidige context hoort bij "nu"; wat daarvoor op
  // een andere pagina of kaart gebeurde, krijgt een grijze kop.
  const groepen = useMemo(
    () => groepeerPerContext(berichten, context),
    [berichten, context],
  );

  const kanChatten = data?.kan_chatten ?? false;
  // Nooit "de reden staat hierboven" zonder reden (sessie 47, 1b): lukt
  // het ophalen niet, dan zegt de copiloot dat zelf, met de fout.
  const redenUit =
    data?.reden_uit ??
    (draad.isError && !data
      ? `De copiloot kon deze pagina niet ophalen: ${vertaalApiFout(draad.error)}`
      : draad.isLoading
        ? null
        : !kanChatten && data
          ? "De copiloot staat uit, zonder opgegeven reden. Meld dit."
          : null);

  // Onderaan houden bij nieuwe beurten en binnenkomende tekst.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [berichten.length, liveTekst, stream.bezig, afgekapt, gestrand, lessen]);

  // Meegroeiend invoerveld: de hoogte volgt de inhoud tot het maximum.
  const lesInvoer = isLeerDit(invoer);
  const lesTeLangNu = lesInvoer && lesTeLang(invoer);
  useLayoutEffect(() => {
    const el = invoerRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = lesInvoer ? INVOER_MAX_HOOGTE_LES : INVOER_MAX_HOOGTE;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [invoer, lesInvoer]);

  // Eén verzendpad voor wat JP typt en wat de kaart vraagt (sessie 73).
  const stuurTekst = useCallback((ruw: string) => {
    const tekst = ruw.trim();
    if (tekst.length === 0 || stream.bezig || !kanChatten) return;
    setInvoer("");
    setFout(null);
    setDierveld(null);
    const gesteldOp = contextSleutel;
    void stream.verstuur(tekst, {
      knop,
      onFout: (melding) => setFout(melding),
      onKlaar: (_bericht, voorstel) => {
        setKnop(null);
        // Alleen op de klantpagina, en alleen als JP nog bij dezelfde klant
        // is: nooit een "Doe dat" voor een dier van een andere klant.
        if (voorstel && context.soort === "klant" && huidigeContext.current === gesteldOp) {
          setDierveld(voorstel);
        }
      },
    });
  }, [knop, kanChatten, stream, context.soort, contextSleutel]);

  // Sessie 84: "leer dit ..." wordt een lesvoorstel, geen copilootvraag.
  const stuurLes = useCallback(
    async (ruw: string) => {
      const tekst = ruw.trim();
      if (tekst.length === 0 || lesBezig || stream.bezig || !kanChatten) return;
      // Te lang: de teller onder het veld zegt waarom er niets gebeurt.
      if (lesTeLang(tekst)) return;
      setInvoer("");
      setFout(null);
      await maakLes(tekst);
    },
    [lesBezig, stream.bezig, kanChatten, maakLes],
  );

  const verstuur = useCallback(() => {
    if (isLeerDit(invoer)) void stuurLes(invoer);
    else stuurTekst(invoer);
  }, [stuurTekst, stuurLes, invoer]);

  // Sessie 73 blok 3: "op de kaart" vanaf de klantkaart. De kaart stuurt een
  // window-event `copiloot:vraag` met {soort, id, tekst}; alleen als dat over
  // deze klant gaat, gaat de tekst langs hetzelfde pad als wat JP typt.
  useEffect(() => {
    const luister = (e: Event) => {
      const detail = (e as CustomEvent<CopilootVraag | undefined>).detail;
      if (!detail || typeof detail.tekst !== "string") return;
      if (context.soort !== "klant" || !context.id) return;
      if (detail.soort !== "klant" || detail.id !== context.id) return;
      if (!kanChatten || stream.bezig) return;
      stuurTekst(detail.tekst);
    };
    window.addEventListener(COPILOOT_VRAAG, luister);
    return () => window.removeEventListener(COPILOOT_VRAAG, luister);
  }, [context.soort, context.id, kanChatten, stream.bezig, stuurTekst]);

  function gaVerder() {
    setFout(null);
    void stream.verstuur("", {
      vervolg: true,
      onFout: (melding) => setFout(melding),
    });
  }

  function probeerOpnieuw() {
    setFout(null);
    void stream.verstuur("", {
      opnieuw: true,
      onFout: (melding) => setFout(melding),
    });
  }

  function springNaarVoorstel() {
    const eerste = openVoorstellen[0];
    if (!eerste) return;
    const el = document.getElementById(`wp-cp-voorstel-${eerste.id}`);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  function opToets(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter verstuurt; Shift+Enter is een nieuwe regel. In de copiloot
    // gaat er nooit iets naar de klant, dus Enter is hier veilig — in de
    // reactietegel is dat bewust anders.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      verstuur();
    }
  }

  const kosten = data?.kosten;

  return (
    <section className="wp-cp" aria-label="Copiloot">
      <div className="wp-cp-kop">
        <Bot size={18} aria-hidden />
        <h2>Copiloot</h2>
        {kosten ? (
          <div className="wp-cp-kosten" data-testid="wp-cp-kosten">
            Copiloot {maandNaam(kosten.maand)}, tot nu toe
            <b>
              {euro(kosten.tot_nu_toe_cents)} van{" "}
              {euro(kosten.maandplafond_cents)}
            </b>
            {typeof kosten.alles_cents === "number" ? (
              <span
                className="wp-cp-kosten-alles"
                data-testid="wp-cp-kosten-alles"
              >
                Alle AI samen deze maand {euro(kosten.alles_cents)}. Het
                plafond remt alleen de copiloot.
              </span>
            ) : null}
            <div
              className="wp-cp-balk"
              data-vol={kosten.maand_vol ? "ja" : "nee"}
              role="meter"
              aria-label="Deel van het AI-maandplafond dat op is"
              aria-valuenow={Math.round(kosten.deel_gebruikt * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <i style={{ width: `${Math.round(kosten.deel_gebruikt * 100)}%` }} />
            </div>
          </div>
        ) : null}
      </div>

      <div className="wp-cp-context" data-testid="wp-cp-context">
        Kijkt mee op: <b>{data?.contextregel ?? "…"}</b>
      </div>

      {openVoorstellen.length > 0 ? (
        <button
          type="button"
          className="wp-cp-wacht"
          onClick={springNaarVoorstel}
          data-testid="wp-cp-strookje"
        >
          <b>
            {openVoorstellen.length}{" "}
            {openVoorstellen.length === 1 ? "voorstel" : "voorstellen"}
          </b>{" "}
          {openVoorstellen.length === 1 ? "wacht" : "wachten"} op je antwoord
          <span>bekijk</span>
        </button>
      ) : null}

      <div
        className="wp-cp-draad"
        ref={scrollRef}
        role="log"
        aria-live="polite"
      >
        {draad.isPending ? (
          <div className="wp-cp-leeg">De copiloot leest mee.</div>
        ) : null}

        {draad.isError ? (
          <div className="wp-cp-melding-fout" role="alert">
            De copiloot is niet bereikbaar. Ververs het scherm; de draad
            blijft bewaard.
          </div>
        ) : null}

        {meldingen.map((melding, i) => (
          <MeldingBericht key={`m-${i}`} melding={melding} />
        ))}

        {/* Sessie 47 (8a): het gesprek blijft scrollbaar, maar wat op een
            andere pagina of kaart gevraagd is staat onder een grijze kop
            "Eerder, op ...". Daaronder de huidige context met zijn eigen
            "Wat opvalt". Een oude "Wat opvalt" blijft nooit staan: zolang
            het antwoord van de vorige context er staat, toont hij niets. */}
        {groepen.oud.map((groep, i) => (
          <div key={`g-${i}`} className="wp-cp-eerder" data-testid="wp-cp-eerder">
            <div className="wp-cp-eerder-kop">
              Eerder, op {groep.label ?? "een andere pagina"}
            </div>
            {groep.berichten.map((bericht) =>
              bericht.rol === "gebruiker" && bericht.vervolg ? null : (
                <Beurt key={bericht.id} bericht={bericht} />
              ),
            )}
          </div>
        ))}

        {groepen.oud.length > 0 ? (
          <div className="wp-cp-nu" data-testid="wp-cp-nu">
            Nu, op {data?.contextregel ?? "deze pagina"}
          </div>
        ) : null}

        {data?.opvallend && !draad.isPlaceholderData ? (
          <div className="wp-cp-opvallend" data-testid="wp-cp-opvallend">
            <b>Wat opvalt:</b> {data.opvallend.regel}
            <br />
            <b>Voorstel:</b> {data.opvallend.voorstel}
          </div>
        ) : null}

        {groepen.nu.map((bericht) =>
          bericht.rol === "gebruiker" && bericht.vervolg ? null : (
            <Beurt key={bericht.id} bericht={bericht} />
          ),
        )}

        {gedaan.map((regel, i) => (
          <div
            key={`gedaan-${i}`}
            className="wp-cp-b wp-cp-b-hij wp-cp-b-melding"
            data-testid="wp-cp-dierveld-gedaan"
          >
            Zojuist gedaan: {regel}
          </div>
        ))}

        {dierveld && context.soort === "klant" && context.id ? (
          <DierveldKaart
            key={`${dierveld.huisdier_id}-${dierveld.tekst}`}
            voorstel={dierveld}
            klantId={context.id}
            onGedaan={(regel) => {
              setDierveld(null);
              setGedaan((oud) => [...oud, regel]);
            }}
            onNiet={() => setDierveld(null)}
          />
        ) : null}

        {voorstellen.map((voorstel) => (
          <VoorstelBericht
            key={voorstel.id}
            voorstel={voorstel}
            context={context}
          />
        ))}

        {lessenLaadFout ? (
          <div className="wp-cp-leeg" data-testid="wp-cp-lessen-laadfout">
            {lessenLaadFout}
          </div>
        ) : null}

        {lessen.map((beurt) => (
          <div key={beurt.sleutel} className="wp-cp-les" data-testid="wp-cp-les">
            {beurt.vraag ? (
              <div className="wp-cp-b wp-cp-b-ik">
                <div className="wp-cp-van">
                  <b>JP</b>
                </div>
                {beurt.vraag}
              </div>
            ) : null}
            {beurt.les ? (
              <LesKaart les={beurt.les} onSluit={() => sluitLes(beurt)} />
            ) : beurt.fout ? (
              <div className="wp-cp-melding-fout" role="alert" data-testid="wp-cp-les-fout">
                {beurt.fout}
              </div>
            ) : (
              <div className="wp-cp-leeg" role="status" data-testid="wp-cp-les-bezig">
                <Loader2 size={14} className="wp-cp-draait" aria-hidden /> De copiloot
                schrijft de les uit en controleert de vangrails.
              </div>
            )}
          </div>
        ))}

        {!draad.isPending &&
        !draad.isError &&
        lessen.length === 0 &&
        berichten.length === 0 &&
        meldingen.length === 0 &&
        voorstellen.length === 0 &&
        !stream.bezig ? (
          <div className="wp-cp-leeg">
            Ik kijk mee op wat je nu open hebt. Vraag me wat je wilt weten,
            of wat ik zou doen.
          </div>
        ) : null}

        {stream.bezig && liveTekst.length > 0 ? (
          <div className="wp-cp-b wp-cp-b-hij" data-testid="wp-cp-live">
            <div className="wp-cp-van">
              <b>Copiloot</b>
            </div>
            {liveTekst}
          </div>
        ) : null}

        {stream.bezig && liveTekst.length === 0 ? (
          <div className="wp-cp-leeg" data-testid="wp-cp-bezig">
            <Loader2 size={14} className="wp-cp-draait" aria-hidden /> De
            copiloot denkt na.
          </div>
        ) : null}

        {afgekapt ? (
          <div className="wp-cp-reden" role="status" data-testid="wp-cp-afgekapt">
            Dit antwoord liep tegen het tokenplafond. De tekst hierboven
            blijft staan.
            <div className="wp-cp-knoppen">
              <button
                type="button"
                className="wp-knop"
                onClick={gaVerder}
                disabled={!kanChatten || stream.bezig}
              >
                Ga verder
              </button>
            </div>
          </div>
        ) : null}

        {gestrand ? (
          <div className="wp-cp-reden" role="alert" data-testid="wp-cp-gestrand">
            Je vraag staat nog open: er kwam geen antwoord terug. De vraag
            is bewaard.
            <div className="wp-cp-knoppen">
              <button
                type="button"
                className="wp-knop"
                onClick={probeerOpnieuw}
                disabled={!kanChatten || stream.bezig}
              >
                Opnieuw proberen
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Waarom de invoer uit staat. Nooit stil verbergen. */}
      {redenUit ? (
        <div className="wp-cp-reden" role="status" data-testid="wp-cp-reden-uit">
          {redenUit}
        </div>
      ) : null}

      {fout ? (
        <div className="wp-cp-melding-fout" role="alert" data-testid="wp-cp-fout">
          {fout}
        </div>
      ) : null}

      <div className="wp-cp-box">
        <textarea
          ref={invoerRef}
          className={lesInvoer ? "les-lang" : undefined}
          rows={1}
          value={invoer}
          onChange={(e) => setInvoer(e.target.value)}
          onKeyDown={opToets}
          disabled={!kanChatten || stream.bezig || lesBezig}
          aria-describedby={lesInvoer ? "wp-cp-les-teller" : undefined}
          aria-label="Vraag of opdracht voor de copiloot"
          placeholder={
            kanChatten
              ? "Vraag of opdracht voor de copiloot"
              : draad.isLoading
                ? "De copiloot laadt…"
                : "De copiloot staat uit; de reden staat hierboven."
          }
        />
        {lesInvoer ? <LesTeller tekst={invoer} id="wp-cp-les-teller" /> : null}
        <div className="wp-cp-rij">
          <button
            type="button"
            className="wp-cp-ikoon"
            disabled
            aria-label="Bijlage toevoegen"
            aria-describedby="wp-cp-reden-bijlage"
          >
            <Plus size={16} aria-hidden />
          </button>
          {/* Sessie 73 (controle blok 1, punt 10): geen modelkeuze (Fable)
              en geen sjabloon (Casetekst) in de werkplek. */}
          <button
            type="button"
            className="wp-cp-ikoon wp-cp-stuur"
            onClick={verstuur}
            disabled={
              !kanChatten ||
              stream.bezig ||
              lesBezig ||
              lesTeLangNu ||
              invoer.trim().length === 0
            }
            aria-label="Versturen"
          >
            {stream.bezig ? (
              <Loader2 size={16} aria-hidden />
            ) : (
              <CornerDownLeft size={16} aria-hidden />
            )}
          </button>
        </div>
        <div
          id="wp-cp-reden-bijlage"
          style={{
            padding: "0 12px 8px",
            fontSize: 11,
            color: "var(--wp-tekst3)",
            lineHeight: 1.4,
          }}
        >
          {REDEN_BIJLAGE}
        </div>
      </div>
    </section>
  );
}

/** Een grijs bericht met de tegenopdracht in gewone taal. */
function MeldingBericht({ melding }: { melding: CopilootMelding }) {
  return (
    <div
      className="wp-cp-b wp-cp-b-hij wp-cp-b-melding"
      data-testid="wp-cp-melding"
    >
      <div className="wp-cp-van">
        <b>Copiloot</b> {tijd(melding.moment)}
        <span className="wp-chip wp-chip-grijs">{melding.chip}</span>
      </div>
      {melding.tekst}
      {melding.afloopt_op ? (
        <div style={{ marginTop: 4 }} data-testid="wp-cp-melding-afloop">
          Loopt af op {datumTijd(melding.afloopt_op)}.
        </div>
      ) : null}
    </div>
  );
}

/** Eén beurt van JP of van de copiloot, met bronnen dichtgeklapt. */
function Beurt({ bericht }: { bericht: CopilootBericht }) {
  if (bericht.rol === "gebruiker") {
    return (
      <div className="wp-cp-b wp-cp-b-ik">
        <div className="wp-cp-van">
          <b>JP</b> {tijd(bericht.created_at)}
        </div>
        {bericht.inhoud}
      </div>
    );
  }

  const bronnen = [
    ...bericht.kennisbronnen.map((b) => `${b.vraag} (${b.categorie})`),
    ...bericht.productbronnen.map((b) => b.naam),
    ...bericht.naslagbronnen.map((b) =>
      b.kop ? `${b.bron_titel} · ${b.kop}` : b.bron_titel,
    ),
  ];

  return (
    <div className="wp-cp-b wp-cp-b-hij">
      <div className="wp-cp-van">
        <b>Copiloot</b> {tijd(bericht.created_at)}
      </div>
      {poetsDierTags(bericht.inhoud)}
      {bericht.concept_tekst ? (
        <div className="wp-cp-concept" data-testid="wp-cp-concept">
          <div className="wp-cp-concept-kop">Voorstel voor de klant</div>
          {bericht.concept_onderwerp ? (
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              Onderwerp: {bericht.concept_onderwerp}
            </div>
          ) : null}
          {poetsDierTags(bericht.concept_tekst)}
          <div style={{ fontSize: 11, marginTop: 6, color: "var(--wp-tekst2)" }}>
            Deze tekst gaat nergens heen. Wil je hem gebruiken, zet hem dan
            zelf in de reactietegel.
          </div>
        </div>
      ) : null}
      {bronnen.length > 0 ? (
        <details className="wp-cp-bronnen">
          <summary data-testid="wp-cp-bronnen">
            Bronnen ({bronnen.length}) <ChevronDown size={12} aria-hidden />
          </summary>
          <ul>
            {bronnen.map((bron, i) => (
              <li key={i}>{bron}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

/**
 * Sessie 73 blok 2: de copiloot stelt voor een dierveld te zetten.
 * "Doe dat" schrijft het via de gewone dierroute (bron "copiloot") en
 * ververst de klantkaart op de pagina; "Niet" haalt de kaart weg.
 */
function DierveldKaart({
  voorstel,
  klantId,
  onGedaan,
  onNiet,
}: {
  voorstel: DierveldVoorstel;
  klantId: string;
  onGedaan: (regel: string) => void;
  onNiet: () => void;
}) {
  const qc = useQueryClient();
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  async function doeDat() {
    if (bezig) return;
    setBezig(true);
    setFout(null);
    try {
      await copilootApi.zetDierveld(voorstel);
      void qc.invalidateQueries({ queryKey: v6Sleutels.klantKaart(klantId) });
      onGedaan(voorstel.gedaan_tekst ?? voorstel.tekst);
    } catch (e) {
      // Een eigen melding van de server tonen we letterlijk; een kale
      // technische fout ("Request failed ...") nooit.
      const detail = (e as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      const reden =
        typeof detail === "string" && detail.trim()
          ? detail.trim().replace(/\.?$/, ".")
          : "de server kon het nu niet opslaan.";
      setFout(
        `${voorstel.huisdier_naam} is niet bijgewerkt: ${reden} Er is niets ` +
          "veranderd; probeer het opnieuw of zet het zelf in het blok Dier.",
      );
      setBezig(false);
    }
  }

  return (
    <div
      className="wp-cp-b wp-cp-b-hij wp-cp-b-voorstel"
      data-testid="wp-cp-dierveld"
    >
      <div className="wp-cp-van">
        <b>Copiloot</b>
        <span className="wp-chip wp-chip-grijs">Dier bijwerken</span>
      </div>
      <div style={{ fontWeight: 600 }}>{voorstel.tekst}</div>
      <div className="wp-cp-knoppen">
        <button
          type="button"
          className="wp-knop wp-knop-primair"
          onClick={() => void doeDat()}
          disabled={bezig}
        >
          Doe dat
        </button>
        <button type="button" className="wp-knop" onClick={onNiet} disabled={bezig}>
          Niet
        </button>
      </div>
      {fout ? (
        <div
          style={{ marginTop: 6, fontSize: 12, color: "var(--wp-rood)" }}
          role="alert"
          data-testid="wp-cp-dierveld-fout"
        >
          {fout}
        </div>
      ) : null}
    </div>
  );
}

/** Een regelvoorstel als bericht met knoppen Ja / Nee. */
function VoorstelBericht({
  voorstel,
  context,
}: {
  voorstel: CopilootVoorstel;
  context: KijkContext;
}) {
  const [bezig, setBezig] = useState(false);
  const [stand, setStand] = useState(voorstel);
  const [fout, setFout] = useState<string | null>(null);

  async function antwoord(ja: boolean) {
    if (bezig) return;
    setBezig(true);
    setFout(null);
    try {
      setStand(await copilootApi.antwoordOpVoorstel(voorstel.id, ja));
    } catch {
      setFout(
        "Het antwoord is niet opgeslagen. Probeer het zo opnieuw; het " +
          "voorstel blijft staan.",
      );
    } finally {
      setBezig(false);
    }
  }

  return (
    <div
      className="wp-cp-b wp-cp-b-hij wp-cp-b-voorstel"
      id={`wp-cp-voorstel-${voorstel.id}`}
      data-testid="wp-cp-voorstel"
      data-context={`${context.soort}:${context.id ?? ""}`}
    >
      <div className="wp-cp-van">
        <b>Copiloot</b> {tijd(voorstel.aangemaakt_op)}
        <span className="wp-chip wp-chip-grijs">Regelvoorstel</span>
      </div>
      <div style={{ fontWeight: 600 }}>{stand.titel}</div>
      {stand.gezien ? (
        <div className="wp-cp-gezien" data-testid="wp-cp-gezien">
          {stand.gezien}
        </div>
      ) : null}
      <div style={{ marginTop: 2 }}>{stand.voorstel}</div>
      {stand.onderbouwing ? (
        <div
          style={{ marginTop: 4, fontSize: 12, color: "var(--wp-tekst2)" }}
        >
          {stand.onderbouwing}
        </div>
      ) : null}

      {stand.open ? (
        <div className="wp-cp-knoppen">
          <button
            type="button"
            className="wp-knop wp-knop-primair"
            onClick={() => void antwoord(true)}
            disabled={bezig}
          >
            Ja, maak de regel
          </button>
          <button
            type="button"
            className="wp-knop"
            onClick={() => void antwoord(false)}
            disabled={bezig}
          >
            Nee
          </button>
          <span>of typ je antwoord hieronder</span>
        </div>
      ) : (
        <div
          style={{ marginTop: 6, fontSize: 12, color: "var(--wp-tekst2)" }}
          data-testid="wp-cp-voorstel-antwoord"
        >
          {stand.status === "goedgekeurd"
            ? "Je zei ja. De regel staat klaar voor de volgende leerronde; " +
              "er is niets aan Sanne's prompt veranderd."
            : "Je zei nee. Het voorstel blijft staan als afgewezen."}
        </div>
      )}

      {fout ? (
        <div
          style={{ marginTop: 6, fontSize: 12, color: "var(--wp-rood)" }}
          role="alert"
        >
          {fout}
        </div>
      ) : null}
    </div>
  );
}
